import { PlusOutlined } from '@ant-design/icons';
import { App, Button, Image } from 'antd';
import { type ChangeEvent, useRef, useState } from 'react';
import { ImageCrop } from '@/components/image/image-crop/ImageCrop';
import { fileToBase64 } from '@/std/base64';
import { IMAGE_EXTENSIONS, IMAGE_TYPES } from '@/std/const/image';
import { uuid } from '@/std/util';
import { ImageListImage } from './ImageListImage';

// Número máximo de imagens que podem ser incluídas
const MAX_IMAGES = 5;
// Tamanho maximo da imagem (em mb)
const MAX_IMAGE_SIZE = 2;

export type ImageListValue = {
    idpk: string;
    path: string;
    principal: boolean;
};

export function ImageList({
    value,
    onChange,
    onDelete,
    onUpdate,
}: {
    value: ImageListValue[];
    onChange: (
        key: string,
        fileName: string,
        base64: string,
        preview: string,
    ) => Promise<number | null>;
    onDelete: (id: string) => void;
    onUpdate: (id: string, principal: boolean) => Promise<boolean>;
}) {
    const { notification } = App.useApp();

    const fotoPrincipal = value.find((image) => image.principal);

    const [visibleCrop, setVisibleCrop] = useState(false);
    const [imageCrop, setImageCrop] = useState<string | undefined>(undefined);
    const [typeFile, setTypeFile] = useState<string | undefined>(undefined);
    const [fileName, setFileName] = useState('');

    const inputFileRef = useRef<HTMLInputElement>(null);

    const imageCropChange = async (file: string): Promise<void> => {
        await onChange(uuid(), fileName, file.replace(`data:${typeFile};base64,`, ''), file);
    };

    const onFileChange = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (!e.target.files?.[0]) {
            return;
        }

        const file = e.target.files[0];
        const file_size = file.size / 1024 / 1024;

        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!extension) {
            return;
        }

        // Validate file type
        if (!IMAGE_EXTENSIONS.includes(extension) || !IMAGE_TYPES.includes(file.type)) {
            notification.error({
                description: 'Por favor, selecione uma imagem válida',
                title: 'Tipo de arquivo inválido',
            });

            if (inputFileRef.current) {
                inputFileRef.current.value = '';
            }

            return;
        }

        // Validate file size
        if (file_size > MAX_IMAGE_SIZE) {
            notification.error({
                description: `O tamanho máximo é ${MAX_IMAGE_SIZE}MB`,
                title: 'Arquivo muito grande',
            });

            if (inputFileRef.current) {
                inputFileRef.current.value = '';
            }

            return;
        }

        const base64 = await fileToBase64(file, false);

        setFileName(file.name);
        setTypeFile(file.type);
        setImageCrop(base64);
        setVisibleCrop(true);

        if (inputFileRef.current) {
            inputFileRef.current.value = '';
        }
    };

    const uploadFile = (): void => {
        if (inputFileRef.current) {
            inputFileRef.current.click();
        }
    };

    const setImageAsPrincipal = async (imagem: ImageListValue): Promise<void> => {
        await onUpdate(imagem.idpk, true);
    };

    const onClose = (): void => {
        setVisibleCrop(false);
        setImageCrop(undefined);
        setTypeFile(undefined);
        setFileName('');

        if (inputFileRef.current) {
            inputFileRef.current.value = '';
        }
    };

    return (
        <>
            {/* Input escondido para a seleção de fotos */}
            <input
                accept={IMAGE_TYPES}
                className='!hidden'
                onChange={onFileChange}
                ref={inputFileRef}
                type='file'
            />

            <div className='flex flex-wrap items-stretch gap-1'>
                <Image.PreviewGroup>
                    {value.map((image) => (
                        <ImageListImage
                            fotoPrincipalIdpk={fotoPrincipal?.idpk}
                            image={image}
                            key={image.idpk}
                            removeImage={onDelete}
                            setImageAsPrincipal={setImageAsPrincipal}
                        />
                    ))}
                </Image.PreviewGroup>

                {value.length < MAX_IMAGES && (
                    <Button
                        className='!h-48 w-[150px] shrink-0'
                        icon={<PlusOutlined />}
                        onClick={uploadFile}
                    >
                        Adicionar foto
                    </Button>
                )}
            </div>

            {/* Tela de edição de uma imagem nova */}
            <ImageCrop
                fileName={fileName}
                fileType={typeFile || ''}
                onChange={imageCropChange}
                onClose={onClose}
                open={visibleCrop}
                src={imageCrop || ''}
            />
        </>
    );
}
